/**
 * Authentication Store
 *
 * Zustand store for managing authentication state, user data, and loading states.
 * Provides centralized state management for authentication across the application.
 */

import { persist } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';

import { createPersistStorage } from './persist-storage';
import {
  changeRoleScope,
  changeSessionScope,
  getSessionEpoch,
} from './session-scope';
import {
  registerAccountAccessor,
  registerAuthSessionCallbacks,
  registerAuthStoreCallbacks,
  syncInitializePreferences,
} from './store-sync';
import { getAuthConfig } from '../di/container';
import { authService } from '../services/auth.service';
import { dayjs } from '../utils/date';

import type {
  User,
  AuthState,
  AuthToken,
  AuthMode,
  DeleteAccountRequest,
  LoginOutcome,
  LoginResponse,
  SessionLoginResponse,
  TwoFactorProof,
  TwoFactorRequired,
} from '../types/auth';
import type { UserPreferences } from '../types/user-preferences.types';
import type { LoginInput, RegisterInput } from '../validation/auth.schemas';

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Type guard to check if response is LoginResponse (token-based)
 */
const isLoginResponse = (
  response: LoginResponse | SessionLoginResponse
): response is LoginResponse => {
  return (
    'token' in response &&
    typeof response.token === 'object' &&
    response.token !== null
  );
};

/**
 * Type guard for a sign-in still waiting for its two-factor code
 */
const isTwoFactorRequired = (
  response: LoginResponse | SessionLoginResponse | TwoFactorRequired
): response is TwoFactorRequired =>
  'two_factor' in response && response.two_factor === true;

// =============================================================================
// STORE INTERFACES
// =============================================================================

export interface AuthStoreState extends AuthState {
  isAuthenticated: boolean;
  user: User | null;

  // Loading states
  isLoading: boolean;
  isLoggingIn: boolean;
  isLoggingOut: boolean;
  isRegistering: boolean;
  isRefreshingToken: boolean;

  // Error states
  error: string | null;
  loginError: string | null;
  registerError: string | null;
  refreshError: string | null;

  // Token management
  token: AuthToken | null;
  refreshToken: string | null;
  tokenExpiry: number | null;

  // Active auth mode is the injected per-platform config (see getAuthMode());
  // `mode` is kept only to satisfy the AuthState contract, not read as state.
  mode: AuthMode; // Alias for compatibility with AuthState

  // Last activity tracking
  lastActivity: number;

  // Auto-refresh settings
  autoRefreshEnabled: boolean;
  refreshThreshold: number; // milliseconds before expiry to refresh
}

export interface AuthStoreActions {
  // Core authentication actions
  /**
   * Sign in with a password. Resolves `'two-factor'` when the account asks
   * for a code first: nothing is signed in yet, and the sign-in finishes with
   * `completeTwoFactorSignIn`.
   */
  login: (credentials: LoginInput) => Promise<LoginOutcome>;
  /**
   * Finish a sign-in `login` left waiting, with a code from the authenticator
   * app or a recovery code. A refusal is thrown for the code step to show,
   * and sets no `loginError`.
   */
  completeTwoFactorSignIn: (proof: TwoFactorProof) => Promise<void>;
  /**
   * Take a sign-in the auth service completed into the store: the user, the
   * bearer in token mode, signed in, preferences synced. Every way in — a
   * password, a provider, a completed pending sign-up — lands here.
   */
  applySignIn: (response: LoginResponse | SessionLoginResponse) => void;
  logout: () => Promise<void>;
  deleteAccount: (proof: DeleteAccountRequest) => Promise<void>;
  register: (data: RegisterInput) => Promise<void>;

  // User management
  setUser: (user: User | null) => void;
  updateUser: (updates: Partial<User>) => void;
  clearUser: () => void;
  /** Resolves whether the server's answer was taken (see the method). */
  fetchUser: () => Promise<boolean>;

  // Token management
  setToken: (token: AuthToken | null) => void;
  setRefreshToken: (refreshToken: string | null) => void;
  refreshAuthToken: () => Promise<void>;
  clearTokens: () => void;

  // State management
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  clearAllErrors: () => void;

  // Session management
  updateLastActivity: () => void;
  checkSessionValidity: () => boolean;

  // Auto-refresh management
  enableAutoRefresh: () => void;
  disableAutoRefresh: () => void;
  setRefreshThreshold: (threshold: number) => void;

  // Store reset
  reset: () => void;
}

export type AuthStore = AuthStoreState & AuthStoreActions;

// =============================================================================
// STORE CONFIGURATION
// =============================================================================

const STORAGE_KEY = 'personal-trainer-auth';
const DEFAULT_REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes before expiry

/**
 * When a token expires, in milliseconds since the epoch.
 *
 * The API's `expires_at` is an absolute moment, so it is used whenever it is
 * there: it cannot be misread. `expires_in` is the fallback, in seconds as the
 * API sends it.
 */
const resolveTokenExpiry = (token: AuthToken | null): number | null => {
  if (!token) return null;

  return token.expires_at
    ? dayjs(token.expires_at).valueOf()
    : dayjs().add(token.expires_in, 'second').valueOf();
};
const SESSION_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours

// =============================================================================
// STORE IMPLEMENTATION
// =============================================================================

export const authStore = createStore<AuthStore>()(
  persist(
    (set, get) => ({
      // =======================================================================
      // INITIAL STATE
      // =======================================================================

      // Auth state
      isAuthenticated: false,
      user: null,

      // Loading states
      isLoading: false,
      isLoggingIn: false,
      isLoggingOut: false,
      isRegistering: false,
      isRefreshingToken: false,

      // Error states
      error: null,
      loginError: null,
      registerError: null,
      refreshError: null,

      // Token management
      token: null,
      refreshToken: null,
      tokenExpiry: null,

      // Auth mode is the injected per-platform config (see getAuthMode());
      // `mode` is kept only for the AuthState contract.
      mode: 'token', // Alias for compatibility with AuthState

      // Last activity tracking
      lastActivity: dayjs().valueOf(),

      // Auto-refresh settings
      autoRefreshEnabled: true,
      refreshThreshold: DEFAULT_REFRESH_THRESHOLD,

      // =======================================================================
      // AUTHENTICATION ACTIONS
      // =======================================================================

      login: async (credentials: LoginInput): Promise<LoginOutcome> => {
        const { setError, clearError, applySignIn } = get();

        // Deliberately leaves the global `isLoading` flag alone, as
        // deleteAccount does: the guest guard turns it into a full-screen
        // spinner, which would unmount the form mid-request and lose what was
        // typed. The form shows its own pending state (`isLoggingIn`).
        set({ isLoggingIn: true, loginError: null });
        clearError();

        try {
          const answer = await authService.login(credentials);
          if (isTwoFactorRequired(answer)) return 'two-factor';

          applySignIn(answer);
          return 'signed-in';
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Login failed';
          setError(errorMessage);
          set({ loginError: errorMessage });
          throw error;
        } finally {
          set({ isLoggingIn: false });
        }
      },

      completeTwoFactorSignIn: async (proof: TwoFactorProof): Promise<void> => {
        const { applySignIn } = get();

        // The code step's form stays mounted, as login's does.
        set({ isLoggingIn: true });

        try {
          applySignIn(await authService.twoFactorChallenge(proof));
        } finally {
          set({ isLoggingIn: false });
        }
      },

      applySignIn: (response: LoginResponse | SessionLoginResponse): void => {
        const { setUser, setToken, setRefreshToken, updateLastActivity } =
          get();

        // An answer with no account would mark the store signed in as nobody.
        if (!response.user) {
          throw new Error('Couldn’t sign you in. Try again.');
        }

        setUser(response.user);

        if (isLoginResponse(response)) {
          setToken(response.token);
          setRefreshToken(response.token.refresh_token || null);
        }

        updateLastActivity();

        set({ isAuthenticated: true });

        // Sync user preferences store with the signed-in user's preferences
        if (response.user.preferences) {
          syncInitializePreferences(response.user.preferences);
        }
      },

      logout: async (): Promise<void> => {
        const {
          setLoading,
          setError,
          clearUser,
          clearTokens,
          updateLastActivity,
        } = get();

        setLoading(true);
        set({ isLoggingOut: true });

        try {
          await authService.logout();

          // Clear all auth data
          clearUser();
          clearTokens();
          updateLastActivity();

          set({ isAuthenticated: false });
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Logout failed';
          setError(errorMessage);
          throw error;
        } finally {
          setLoading(false);
          set({ isLoggingOut: false });
        }
      },

      deleteAccount: async (proof: DeleteAccountRequest): Promise<void> => {
        // Deliberately does NOT toggle the global `isLoading` flag. That flag
        // drives the route-level auth guard's full-screen spinner, which would
        // unmount the confirm modal (and its inline error) mid-request — the
        // exact "spinner flash then the page resets with no error" failure.
        // The modal owns its own pending state, mirroring the local-state
        // password-change flow.
        //
        // On failure the rejection propagates untouched so the modal can
        // surface it and keep the user in place; the teardown below only runs
        // once the account is actually gone, so a refused password or
        // confirmation leaves auth state fully intact.
        await authService.deleteAccount(proof);

        const { clearUser, clearTokens } = get();
        clearUser();
        clearTokens();
        set({ isAuthenticated: false });
      },

      register: async (data: RegisterInput): Promise<void> => {
        const { setError, clearError, applySignIn } = get();

        // Deliberately leaves the global `isLoading` flag alone, as
        // deleteAccount does: the guest guard turns it into a full-screen
        // spinner, which would unmount the form mid-request and lose what was
        // typed. The form shows its own pending state (`isRegistering`).
        set({ isRegistering: true, registerError: null });
        clearError();

        try {
          // The website is answered 201 with no body and a signed-in
          // session; an app is answered a bearer, which the service has
          // stored. Either way the new account is read back with it.
          const answer = await authService.register(data);
          const token =
            answer && typeof answer === 'object' && 'token' in answer
              ? answer.token
              : undefined;

          // The account exists from here on. Failing to read it back means
          // this device isn't signed in to it, not that registering failed.
          let user: User | null = null;
          try {
            user = await authService.getSessionUser();
          } catch {
            user = null;
          }
          if (!user) {
            throw new Error(
              'Your account was created, but we couldn’t sign you in. Log in with your new password.'
            );
          }

          applySignIn(token ? { user, token } : { user });
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Registration failed';
          setError(errorMessage);
          set({ registerError: errorMessage });
          throw error;
        } finally {
          set({ isRegistering: false });
        }
      },

      // =======================================================================
      // USER MANAGEMENT
      // =======================================================================

      setUser: (user: User | null): void => {
        set((state) => ({
          ...state,
          user,
          isAuthenticated: user !== null,
        }));
      },

      updateUser: (updates: Partial<User>): void => {
        set((state) => ({
          ...state,
          user: state.user ? { ...state.user, ...updates } : null,
        }));
      },

      clearUser: (): void => {
        set((state) => ({
          ...state,
          user: null,
          isAuthenticated: false,
        }));
      },

      /**
       * Reads whose session the server holds and applies it: how the website
       * picks up a session made during a redirect (a social sign-in), how it
       * refreshes the user after a change the server made (a subscription),
       * and how the website and the apps show a change made elsewhere
       * (`session-user`). No session signs the store out, quietly. A failure
       * to ask leaves the store as it was, and an answer that arrives after a
       * boundary (a sign-out, another account) belongs to a finished session
       * and is dropped. It resolves whether the answer was taken, so a caller
       * asking on someone's behalf ("Check again") can tell a failure from an
       * answer.
       */
      fetchUser: async (): Promise<boolean> => {
        const epoch = getSessionEpoch();
        let user: User | null;
        try {
          user = await authService.getSessionUser();
        } catch (error) {
          console.warn('[auth] Could not read the session user:', error);
          return false;
        }
        if (getSessionEpoch() !== epoch) return false;

        if (user) get().applySignIn({ user });
        else get().clearUser();
        return true;
      },

      // =======================================================================
      // TOKEN MANAGEMENT
      // =======================================================================

      setToken: (token: AuthToken | null): void => {
        set((state) => ({
          ...state,
          token,
          tokenExpiry: resolveTokenExpiry(token),
        }));
      },

      setRefreshToken: (refreshToken: string | null): void => {
        set((state) => ({
          ...state,
          refreshToken,
        }));
      },

      refreshAuthToken: async (): Promise<void> => {
        const {
          setLoading,
          setError,
          clearError,
          refreshToken,
          setToken,
          updateLastActivity,
        } = get();

        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        setLoading(true);
        set({ isRefreshingToken: true, refreshError: null });
        clearError();

        try {
          const token = await authService.refreshToken();

          setToken(token);
          updateLastActivity();
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Token refresh failed';
          setError(errorMessage);
          set({ refreshError: errorMessage });
          throw error;
        } finally {
          setLoading(false);
          set({ isRefreshingToken: false });
        }
      },

      clearTokens: (): void => {
        set((state) => ({
          ...state,
          token: null,
          refreshToken: null,
          tokenExpiry: null,
        }));
      },

      // =======================================================================
      // STATE MANAGEMENT
      // =======================================================================

      setLoading: (loading: boolean): void => {
        set((state) => ({
          ...state,
          isLoading: loading,
        }));
      },

      setError: (error: string | null): void => {
        set((state) => ({
          ...state,
          error,
        }));
      },

      clearError: (): void => {
        set((state) => ({
          ...state,
          error: null,
        }));
      },

      clearAllErrors: (): void => {
        set((state) => ({
          ...state,
          error: null,
          loginError: null,
          registerError: null,
          refreshError: null,
        }));
      },

      // =======================================================================
      // SESSION MANAGEMENT
      // =======================================================================

      updateLastActivity: (): void => {
        set((state) => ({
          ...state,
          lastActivity: dayjs().valueOf(),
        }));
      },

      checkSessionValidity: (): boolean => {
        const { lastActivity, tokenExpiry } = get();
        const now = dayjs().valueOf();

        // The inactivity timeout belongs to session auth, where the web app
        // feeds `lastActivity` from real use. Token auth never does — on a
        // phone it is only ever the moment of sign-in — so the same check
        // there is "24 hours since signing in", which ends every session a
        // day old. A bearer carries its own lifetime, checked next, and that
        // is the whole rule for token auth.
        if (
          getAuthMode() === 'session' &&
          now - lastActivity > SESSION_TIMEOUT
        ) {
          return false;
        }

        // Check if token has expired
        if (tokenExpiry && now > tokenExpiry) {
          return false;
        }

        return true;
      },

      // =======================================================================
      // AUTO-REFRESH MANAGEMENT
      // =======================================================================

      enableAutoRefresh: (): void => {
        set((state) => ({
          ...state,
          autoRefreshEnabled: true,
        }));
      },

      disableAutoRefresh: (): void => {
        set((state) => ({
          ...state,
          autoRefreshEnabled: false,
        }));
      },

      setRefreshThreshold: (threshold: number): void => {
        set((state) => ({
          ...state,
          refreshThreshold: threshold,
        }));
      },

      // =======================================================================
      // STORE RESET
      // =======================================================================

      reset: (): void => {
        set(() => ({
          // Reset to initial state
          isAuthenticated: false,
          user: null,
          isLoading: false,
          isLoggingIn: false,
          isLoggingOut: false,
          isRegistering: false,
          isRefreshingToken: false,
          error: null,
          loginError: null,
          registerError: null,
          refreshError: null,
          token: null,
          refreshToken: null,
          tokenExpiry: null,
          lastActivity: dayjs().valueOf(),
          autoRefreshEnabled: true,
          refreshThreshold: DEFAULT_REFRESH_THRESHOLD,
        }));
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createPersistStorage(),
      partialize: (state): Partial<AuthStoreState> => ({
        // Only persist essential auth data
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        // NOT the bearer or the refresh token. Token storage is the one copy
        // on disk — SecureStore on mobile, kept there precisely so the bearer
        // stays out of plain storage — and this store's persistence is plain
        // storage (AsyncStorage). Nothing reads the store's copy back: the
        // session is judged by `tokenExpiry`, and a refresh reads the refresh
        // token from token storage. The in-memory fields stay for the life of
        // the process, as before.
        tokenExpiry: state.tokenExpiry,
        // NOTE: authMode is intentionally NOT persisted — it's a per-platform
        // constant sourced from the injected auth config (registerAuthConfig),
        // not runtime state. Persisting it let a stale value rehydrate and pick
        // the wrong auth strategy (e.g. mobile resolving the session/CSRF flow).
        lastActivity: state.lastActivity,
        autoRefreshEnabled: state.autoRefreshEnabled,
        refreshThreshold: state.refreshThreshold,
      }),
      version: 1,
    }
  )
);

// =============================================================================
// STORE SELECTORS
// =============================================================================

/**
 * Selector for authentication status
 */
export const selectIsAuthenticated = (state: AuthStore): boolean =>
  state.isAuthenticated;

/**
 * Selector for current user
 */
export const selectUser = (state: AuthStore): User | null => state.user;

/**
 * Selector for loading states
 */
export const selectLoading = (
  state: AuthStore
): {
  isLoading: boolean;
  isLoggingIn: boolean;
  isLoggingOut: boolean;
  isRegistering: boolean;
  isRefreshingToken: boolean;
} => ({
  isLoading: state.isLoading,
  isLoggingIn: state.isLoggingIn,
  isLoggingOut: state.isLoggingOut,
  isRegistering: state.isRegistering,
  isRefreshingToken: state.isRefreshingToken,
});

/**
 * Selector for error states
 */
export const selectErrors = (
  state: AuthStore
): {
  error: string | null;
  loginError: string | null;
  registerError: string | null;
  refreshError: string | null;
} => ({
  error: state.error,
  loginError: state.loginError,
  registerError: state.registerError,
  refreshError: state.refreshError,
});

/**
 * Selector for token information
 */
export const selectTokenInfo = (
  state: AuthStore
): {
  token: AuthToken | null;
  refreshToken: string | null;
  tokenExpiry: number | null;
  hasValidToken: boolean;
} => ({
  token: state.token,
  refreshToken: state.refreshToken,
  tokenExpiry: state.tokenExpiry,
  hasValidToken: state.tokenExpiry
    ? dayjs().valueOf() < state.tokenExpiry
    : false,
});

/**
 * Selector for session information
 */
export const selectSessionInfo = (
  state: AuthStore
): {
  lastActivity: number;
  isSessionValid: boolean;
  autoRefreshEnabled: boolean;
  refreshThreshold: number;
} => ({
  lastActivity: state.lastActivity,
  isSessionValid: state.checkSessionValidity(),
  autoRefreshEnabled: state.autoRefreshEnabled,
  refreshThreshold: state.refreshThreshold,
});

/**
 * Selector for user preferences
 * Returns the user's preferences or null if not authenticated
 */
export const selectUserPreferences = (
  state: AuthStore
): UserPreferences | null => state.user?.preferences ?? null;

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Check if the current session is valid
 */
export const isSessionValid = (): boolean => {
  return authStore.getState().checkSessionValidity();
};

/**
 * Get the current authentication token
 */
export const getCurrentToken = (): AuthToken | null => {
  return authStore.getState().token;
};

/**
 * Get the current user
 */
export const getCurrentUser = (): User | null => {
  return authStore.getState().user;
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return authStore.getState().isAuthenticated;
};

/**
 * Get authentication mode — sourced from the injected platform auth config
 * (`registerAuthConfig`). Auth mode is a per-platform constant, not runtime
 * state. Throws if no platform has registered a config (a wiring bug).
 */
export const getAuthMode = (): AuthMode => {
  return getAuthConfig().mode;
};

/**
 * Get current user preferences
 * Returns the user's preferences or null if not authenticated
 */
export const getUserPreferences = (): UserPreferences | null => {
  return authStore.getState().user?.preferences ?? null;
};

/**
 * Get current user features
 * Returns the user's features or null if not authenticated
 */
export const getUserFeatures = (): Record<string, boolean> | null => {
  return authStore.getState().user?.features ?? null;
};

/**
 * Name of a backend feature flag — the keys of the `features` map returned by
 * the auth endpoint (e.g. `'external_exercises'`).
 */
export type FeatureName = keyof NonNullable<User['features']>;

/**
 * Check whether a backend feature flag is enabled for the current user.
 *
 * Reads a one-off snapshot of the store, so it is NOT reactive — use it for
 * imperative checks (guards, event handlers). For UI that should re-render when
 * the user/features change, use {@link selectIsFeatureEnabled} with the store
 * hook instead.
 */
export const isFeatureEnabled = (feature: FeatureName): boolean => {
  return Boolean(authStore.getState().user?.features?.[feature]);
};

/**
 * Selector factory for reactive feature-flag checks inside components.
 *
 * @example
 * const canImport = useAuthStore(selectIsFeatureEnabled('external_exercises'));
 */
export const selectIsFeatureEnabled =
  (feature: FeatureName) =>
  (state: AuthStore): boolean =>
    Boolean(state.user?.features?.[feature]);

// =============================================================================
// STORE SYNC REGISTRATION
// =============================================================================

// Register auth store callbacks for cross-store synchronization
registerAuthStoreCallbacks({
  updateUser: (updates) => authStore.getState().updateUser(updates),
  getUser: () => authStore.getState().user,
});

// The service caches key every entry by the account that fetched it.
registerAccountAccessor(() => authStore.getState().user?.username ?? null);

// Register the session callbacks the bearer lifecycle drives: a refresh has to
// reach `tokenExpiry`, or the store goes on judging the session by the bearer
// that was replaced.
registerAuthSessionCallbacks({
  applyRefreshedToken: (token) => {
    const { setToken, setRefreshToken } = authStore.getState();
    setToken(token);
    if (token.refresh_token) setRefreshToken(token.refresh_token);
  },
  endSession: () => {
    authStore.getState().reset();
  },
});

// =============================================================================
// SESSION BOUNDARY
// =============================================================================

// The account, not the flag: every path that ends a session sets `user: null`,
// and a successful `fetchUser` can swap one account for another without
// `isAuthenticated` ever flipping. `username` is the only stable key a `User`
// has (there is no id, and the username never changes). This runs inside the
// `set()` that changed the user, before any effect of the new session.
//
// A page load fires nothing on web: persisted auth hydrates synchronously when
// the store is created, above. Mobile's later `rehydrate()` fires none → A
// once, which is harmless — nothing scoped to the account has loaded yet.
authStore.subscribe((state, prior) => {
  const previous = prior.user?.username ?? null;
  const next = state.user?.username ?? null;
  if (next !== previous) changeSessionScope({ previous, next });
  // The same account, switched between client and trainer (from Settings, or
  // read again after a switch elsewhere): its role-shaped data goes too.
  else if (next !== null && state.user?.role !== prior.user?.role) {
    changeRoleScope(next);
  }
});
